'use client';

import type { InteractionType } from '~/model/InteractionType';
import type { QuestionType } from '~/model/QuestionType';
import { VpButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from '@/app/components/Loader';
import { useComments } from '@/app/hooks/useComments';
import { isPaginationHasMorePage } from '@/app/utils/entities/collection';
import { scrollToElement } from '~/utils/utils';
import { CommentBanner } from './CommentBanner/CommentBanner';
import { Item as Answer } from './Item';
import { ItemPost } from './ItemPost';
import { ThreadWrapper } from './ThreadWrapper';

export const Discussion = ({
  question,
  locked,
  interaction,
}: {
  question: QuestionType;
  locked: boolean;
  interaction?: InteractionType;
}) => {
  const t = useTranslations('DISCUSSION');
  const searchParams = useSearchParams();

  const {
    answers,
    deleteReaction,
    getAnswers,
    getComments,
    pagination,
    postComment,
    postReaction,
    processing,
  } = useComments({ interaction, question });

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [hasToLoadMore, setHasToLoadMore] = useState(false);

  const forChildren = interaction?.for_children;

  const hasMore = isPaginationHasMorePage(pagination);
  const focusedItem = searchParams.get('c') && Number.parseInt(searchParams.get('c'), 10);
  const focusedSubItem = searchParams.get('sc') && Number.parseInt(searchParams.get('sc'), 10);

  useEffect(() => {
    getAnswers();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id]);

  const answersList = answers?.length > 0
    ? (
        <Loader loading={processing} centered>
          <InfiniteScroll
            dataLength={answers.length}
            next={() => hasToLoadMore && getAnswers(true)}
            hasMore={hasMore}
            loader={null}
            style={{ height: 'initial', overflow: 'visible' }}
          >
            {answers.map(idea => (
              <Answer
                forChildren={forChildren}
                key={idea['@id']}
                item={idea}
                getComments={getComments}
                postComment={postComment}
                postReaction={postReaction}
                deleteReaction={deleteReaction}
                processing={processing}
                focusedSubItem={focusedSubItem}
                locked={locked}
              />
            ))}
          </InfiniteScroll>
          {!hasToLoadMore && hasMore && (
            <div className="thread-idea__comments-list__see-more-action">
              <VpButton
                variant="secondary"
                onClick={async () => {
                  await getAnswers(true).then(() => setHasToLoadMore(true));
                }}
              >
                {t('SEE_MORE_COMMENT')}
              </VpButton>
            </div>
          )}
        </Loader>
      )
    : !locked && <CommentBanner interaction={interaction} />;

  // autoscroll to comment
  // if found in "c" query param
  useLayoutEffect(() => {
    if (answers && !hasAutoScrolled && focusedItem) {
      const anchor = `c-${focusedItem}`;
      const flattenAnswers = answers.reduce((acc, item) => {
        acc.push(item);
        if (item.comments?.length > 0)
          acc.push(...item.comments);
        return acc;
      }, []);
      if (flattenAnswers?.some(i => i.id === focusedItem)) {
        // if corresponding id is present in the dom
        scrollToElement(anchor);
        setHasAutoScrolled(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers?.length]);

  return (
    <ThreadWrapper contributionsCount={answers?.length}>
      {question
        ? (
            <>
              {!locked && (
                <ItemPost
                  forChildren={forChildren}
                  postComment={postComment}
                  processing={processing}
                  id="post-idea"
                />
              )}
              {answers ? answersList : <Loader centered />}
            </>
          )
        : (
            <CommentBanner interaction={interaction} />
          )}
    </ThreadWrapper>
  );
};
